import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HelpIcon } from "@/components/ui/help-icon";
import { useAmiCommand } from "@/hooks/use-ami-command";
import { Send, Terminal, Trash2 } from "lucide-react";

export default function AmiConsolePage() {
  const [command, setCommand] = useState("");
  const [history, setHistory] = useState<{ command: string; output: string; success: boolean }[]>([]);
  const [recallIndex, setRecallIndex] = useState<number | null>(null);
  const mutate = useAmiCommand();
  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight });
  }, [history]);

  const handleSend = () => {
    const cmd = command.trim();
    if (!cmd || mutate.isPending) return;

    setRecallIndex(null);
    mutate.mutate(cmd, {
      onSuccess: (data) => {
        setHistory((prev) => [...prev, { command: data.command, output: data.output, success: data.success }]);
        setCommand("");
        inputRef.current?.focus();
      },
      onError: (e) => {
        setHistory((prev) => [...prev, { command: cmd, output: (e as Error).message, success: false }]);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend();
    if (e.key === "ArrowUp" && history.length > 0) {
      e.preventDefault();
      const next =
        recallIndex === null ? history.length - 1 : Math.max(0, recallIndex - 1);
      setRecallIndex(next);
      setCommand(history[next].command);
    }
    if (e.key === "ArrowDown" && recallIndex !== null) {
      e.preventDefault();
      const next = recallIndex + 1;
      if (next >= history.length) {
        setRecallIndex(null);
        setCommand("");
      } else {
        setRecallIndex(next);
        setCommand(history[next].command);
      }
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-background px-4">
        <Terminal size={14} className="text-muted-foreground" />
        <span className="text-xs font-medium text-foreground">AMI Console</span>
        <HelpIcon text="Runs Asterisk CLI commands through the Manager Interface (AMI). Try: pjsip show endpoints, core show channels, pjsip show registrations. Arrow-up recalls previous commands." />
        <div className="flex-1" />
        {history.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setHistory([])}
          >
            <Trash2 size={12} />
            Clear
          </Button>
        )}
      </div>

      <div
        ref={outputRef}
        className="min-h-0 flex-1 overflow-y-auto bg-card px-4 py-3 font-mono text-xs leading-5 scrollbar-thin"
      >
        {history.length === 0 ? (
          <div className="flex h-full select-none flex-col items-center justify-center gap-1 text-muted-foreground">
            <Terminal size={20} />
            <p>Run an Asterisk CLI command to get started.</p>
            <p className="text-muted-foreground/70">
              e.g. <span className="text-foreground">pjsip show endpoints</span>
            </p>
          </div>
        ) : (
          history.map((entry, i) => (
            <div key={i} className="mb-3">
              <div className="text-muted-foreground">
                <span className="select-none text-primary">$ </span>
                {entry.command}
              </div>
              <pre
                className={`mt-0.5 whitespace-pre-wrap ${
                  entry.success ? "text-foreground" : "text-destructive-text"
                }`}
              >
                {entry.output || "(no output)"}
              </pre>
            </div>
          ))
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2 border-t border-border bg-background px-4 py-3">
        <span className="select-none font-mono text-sm text-primary">$</span>
        <Input
          ref={inputRef}
          value={command}
          onChange={(e) => {
            setCommand(e.target.value);
            setRecallIndex(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder="pjsip show endpoints"
          className="flex-1 font-mono text-sm"
          autoFocus
        />
        <Button
          onClick={handleSend}
          disabled={!command.trim()}
          loading={mutate.isPending}
          size="sm"
          className="gap-1.5"
        >
          <Send size={14} />
          Send
        </Button>
      </div>
    </div>
  );
}
